import { useBitrix } from '@context/useBitrixUser/useBitrix';
import { CircularProgress } from '@material-ui/core';
import { DocumentFile, Field } from '@services/requests/companyProfileService/interface';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { ChangeNotification } from '../../ChangeNotification';
import { DocumentServiceType, getDocumentService } from './helpers';
import { getDealIdFromPath } from '@pages/DealPage/helpers';

interface DocumentItemProps {
  document: Field<DocumentFile | DocumentFile[] | null>;
  code: string;
  uploadText: string;
  editText: string;
  isAllowEdit: boolean;
  isArrayDocuments: boolean;
  serviceType: DocumentServiceType;
}

function DocumentItem({
  document,
  code,
  editText,
  uploadText,
  isAllowEdit,
  isArrayDocuments,
  serviceType,
}: DocumentItemProps) {
  const [fileData, setFileData] = useState<DocumentFile | DocumentFile[] | null>(document.value);
  const [isChangeNotificationOpen, setIsChangeNotificationOpen] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const { useBitrixUser } = useBitrix();
  const [{ bitrixUser }] = useBitrixUser().useState();

  const { enqueueSnackbar } = useSnackbar();
  const { getRootProps, getInputProps } = useDropzone({
    disabled: isUploading,
    maxFiles: 1,
    multiple: false,
    onDrop: async (files: File[]) => {
      if (files.length < 1) {
        return;
      }

      setIsUploading(true);
      const file = files[0];
      const uploadService = getDocumentService(serviceType);

      if (uploadService === null) {
        setIsUploading(false);
        return;
      }

	  const dealId = getDealIdFromPath();
      const result = await uploadService(
        serviceType === 'company-profile' ? bitrixUser.id : dealId,
        {
          fieldCode: code,
          file,
        }
      );
      setIsUploading(false);

      if ('errors' in result) {
        enqueueSnackbar(result.errors.join(' '), { variant: 'error' });
      }

      if (!result.success) {
        return;
      }

      setIsChangeNotificationOpen(true);
      isArrayDocuments
        ? setFileData((prevState) =>
            prevState === null ? [result.data] : [...(prevState as DocumentFile[]), result.data]
          )
        : setFileData(result.file);
    },
  });

  if (fileData === null && !document.editable) {
    return null;
  }

  const isText = document?.type === 'text';

  return (
    <>
      <div className="document-wrapper">
        {!fileData ? (
          <span className={clsx('document-item')}>{document.title}</span>
        ) : Array.isArray(fileData) ? (
          <div className={clsx('multi-document-list', { text: isText })}>
            {isText && <span className={clsx('document-item text')}>{document.title}</span>}
            {fileData.map((file, index) =>
              isText ? (
                <span
                  key={'text-' + index}
                  className={clsx('document-item text', { 'first-item': index === 0 })}
                >
                  {file as unknown as string}
                  {fileData.length - 1 !== index && ','}
                </span>
              ) : (
                <a
                  key={'file-' + file.id}
                  href={file.link}
                  target="_blank"
                  rel="noreferrer"
                  className={clsx('document-item')}
                >
                  {document.title} ({index + 1})
                </a>
              )
            )}
          </div>
        ) : (
          <a href={fileData.link} target="_blank" rel="noreferrer" className={'document-item'}>
            {document.title}
          </a>
        )}
        {document.editable && isAllowEdit && !isText && (
          <div {...getRootProps({ className: `upload-file-btn` })}>
            <span role="button" className="document-button">
              {isUploading && (
                <CircularProgress color="inherit" size={12} sx={{ marginRight: '10px' }} />
              )}
              {!fileData ? uploadText : editText}
            </span>
            <input {...getInputProps()} style={{ display: 'none' }} />
          </div>
        )}
      </div>
      <ChangeNotification
        needReviewType={false}
        isOpen={isChangeNotificationOpen}
        onClose={() => setIsChangeNotificationOpen(false)}
      />
    </>
  );
}

export { DocumentItem };
